/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useState, useEffect } from "react";

// Material Dashboard 2 React components
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import Footer from "../../examples/Footer";
import ComplexStatisticsCard from "../../examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Wildfire Risk Map Component
import WildfireRiskMap from "../../components/WildfireRiskMap";

// Adaptive Threshold Section Component
import AdaptiveThresholdSection from "../../components/AdaptiveThresholdSection";

// Satellite Context Section Component
import SatelliteContextSection from "../../components/SatelliteContextSection";

// Data
import reportsBarChartData from "./data/reportsBarChartData";
import reportsLineChartData from "./data/reportsLineChartData";

// Config
import config from "../../config";

function Wildfire() {
  const { sales, tasks } = reportsLineChartData;
  
  // State for weather data
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [location, setLocation] = useState("California,USA");
  const [date, setDate] = useState("");
  
  // State for wildfire prediction results
  const [wildfirePrediction, setWildfirePrediction] = useState(null);
  
  // State for custom location feature
  const [customLocationInput, setCustomLocationInput] = useState("");
  const [customLocationResult, setCustomLocationResult] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState("");
  
  // Function to format numbers with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null || num === "--") return "--";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  
  // Function to fetch wildfire prediction data from the backend
  const fetchWildfireData = async () => {
    setWeatherLoading(true);
    setWeatherError("");
    
    try {
      let url = `${config.apiBaseUrl}/api/predict-wildfire-from-weather?location=${encodeURIComponent(location)}`;
      if (date) {
        url += `&date=${encodeURIComponent(date)}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch wildfire data');
      }
      
      const data = await response.json();
      console.log('Wildfire data:', data);
      
      setWeatherData(data.weather);
      setWildfirePrediction(data.wildfire);
      
    } catch (error) {
      console.error('Error fetching wildfire data:', error);
      setWeatherError(error.message);
    } finally {
      setWeatherLoading(false);
    }
  };
  
  // Function to fetch wildfire data for custom location
  const fetchCustomLocationData = async () => {
    if (!customLocationInput.trim()) {
      setCustomError("Please enter a city name or zip code");
      return;
    }
    
    setCustomLoading(true);
    setCustomError("");
    
    try {
      const url = `${config.apiBaseUrl}/api/predict-wildfire-from-weather?location=${encodeURIComponent(customLocationInput)}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch wildfire data');
      }
      
      const data = await response.json();
      console.log('Custom location wildfire data:', data);
      
      setCustomLocationResult(data);
      
    } catch (error) {
      console.error('Error fetching custom location wildfire data:', error);
      setCustomError('Could not fetch wildfire data for that location. Try a valid US city or zip code.');
    } finally {
      setCustomLoading(false);
    }
  };
  
  // Load wildfire data when component mounts or location/date changes
  useEffect(() => {
    fetchWildfireData();
  }, [location, date]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Page Title */}
        <MDBox mt={2} mb={2}>
          <MDTypography variant="h3" fontWeight="bold" color="dark" textAlign="center">
            Wildfire Risk Predictor
          </MDTypography>
          <MDTypography variant="body1" color="text" textAlign="center" mt={2} mb={1}>
            Estimates wildfire danger from current weather conditions using the Canadian Fire Weather Index system and a derived ignition-probability model.
          </MDTypography>
          <MDBox textAlign="center" mt={2}>
            <MDTypography variant="caption" color="text" fontStyle="italic">
              Note: FWI values early in the year are based on conditions since January 1, 2026 and may not reflect long-term fuel dryness.
            </MDTypography>
          </MDBox>
        </MDBox>
        
        {/* Normal Mode Title */}
        <MDBox mt={4.5} mb={2}>
          <MDTypography variant="h4" fontWeight="medium" color="info" textTransform="uppercase" textAlign="left">
            CALIFORNIA WEATHER TODAY
          </MDTypography>
        </MDBox>
        
        {/* Current Weather Data Display */}
        <MDBox mb={3}>
          <Card>
            <MDBox p={3}>
              {weatherLoading && (
                <MDBox textAlign="center" mb={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Loading California weather data...
                  </MDTypography>
                </MDBox>
              )}
              
              {weatherError && (
                <MDBox textAlign="center" mb={2}>
                  <MDTypography variant="body2" color="error">
                    {weatherError}
                  </MDTypography>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={fetchWildfireData}
                    sx={{ mt: 2 }}
                    disabled={weatherLoading}
                  >
                    <MDTypography variant="button" fontWeight="bold" color="white">
                      Retry
                    </MDTypography>
                  </Button>
                </MDBox>
              )}
              
              {weatherData && !weatherError && (
                <>
                  <MDBox textAlign="center" mb={3}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Current Weather for {location}
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      Date: {date || "Today"}
                    </MDTypography>
                  </MDBox>
                  
                  <Grid container spacing={3}>
                    {/* Temperature */}
                    <Grid item xs={12} sm={6} md={2}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Temperature
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {weatherData.temperature}°F
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Humidity */}
                    <Grid item xs={12} sm={6} md={2}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Humidity
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {weatherData.humidity}%
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Wind Speed */}
                    <Grid item xs={12} sm={6} md={2}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Wind Speed
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {weatherData.windspeed} mph
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Precipitation */}
                    <Grid item xs={12} sm={6} md={2}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Precipitation
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {weatherData.precip} in
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Solar Radiation */}
                    {weatherData.solarradiation !== undefined && (
                      <Grid item xs={12} sm={6} md={2}>
                        <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                          <MDTypography variant="button" color="text" fontWeight="medium">
                            Solar Radiation
                          </MDTypography>
                          <MDTypography variant="h5" fontWeight="medium" mt={1}>
                            {weatherData.solarradiation} W/m²
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    )}
                    
                    {/* Day Length */}
                    {weatherData.dayLength !== undefined && (
                      <Grid item xs={12} sm={6} md={2}>
                        <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                          <MDTypography variant="button" color="text" fontWeight="medium">
                            Day Length
                          </MDTypography>
                          <MDTypography variant="h5" fontWeight="medium" mt={1}>
                            {weatherData.dayLength.toFixed(2)} hours
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    )}
                  </Grid>
                  
                  <MDBox textAlign="center" mt={3}>
                    <MDTypography variant="body1" color="text" fontWeight="medium">
                      These current California weather values are used to predict wildfire risk below
                    </MDTypography>
                    <Button 
                      variant="outlined" 
                      color="info" 
                      onClick={fetchWildfireData}
                      sx={{
                        mt: 2,
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                          borderColor: '#1565c0'
                        }
                      }}
                      disabled={weatherLoading}
                    >
                      <MDTypography variant="button" fontWeight="medium" color="white">
                        {weatherLoading ? "Refreshing..." : "Refresh Weather Data"}
                      </MDTypography>
                    </Button>
                  </MDBox>
                </>
              )}
            </MDBox>
          </Card>
        </MDBox>

        {/* Prediction Results Title */}
        <MDBox mt={2} mb={2}>
          <MDTypography variant="h5" fontWeight="medium" color="dark" textAlign="center">
            Wildfire Risk Prediction Based on Current California Weather
          </MDTypography>
        </MDBox>

        {/* Wildfire Prediction Statistics Cards */}
        <Grid container spacing={3} marginTop={5}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="warning"
                icon="local_fire_department"
                title="Fire Weather Index (FWI)"
                count={wildfirePrediction ? formatNumber(wildfirePrediction.FWI.toFixed(2)) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="error"
                icon="trending_up"
                title="Standardized FWI (FWI_z)"
                count={wildfirePrediction ? formatNumber(wildfirePrediction.FWI_z.toFixed(2)) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color={
                  wildfirePrediction?.risk_category === "Extreme" ? "error" :
                  wildfirePrediction?.risk_category === "High" ? "warning" :
                  wildfirePrediction?.risk_category === "Moderate" ? "info" : "success"
                }
                icon="warning"
                title="Risk Category"
                count={wildfirePrediction ? wildfirePrediction.risk_category : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="whatshot"
                title="Ignition Probability"
                count={wildfirePrediction ? `${(wildfirePrediction.ignition_probability * 100).toFixed(2)}%` : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
        
        {/* California Wildfire Risk Map */}
        <WildfireRiskMap />
        
        {/* Custom Location Section */}
        <MDBox mt={6} mb={3}>
          <MDTypography variant="h4" fontWeight="medium" color="info" textTransform="uppercase" textAlign="left" mb={3}>
            Check Fire Danger for Another Location
          </MDTypography>
          
          <Card>
            <MDBox p={3}>
              <MDTypography variant="body1" color="text" mb={3}>
                Enter a US city or zip code to check current wildfire danger for that location
              </MDTypography>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="City or Zip Code (US)"
                    placeholder="e.g., Los Angeles, CA or 90064"
                    value={customLocationInput}
                    onChange={(e) => setCustomLocationInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        fetchCustomLocationData();
                      }
                    }}
                    disabled={customLoading}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={fetchCustomLocationData}
                    disabled={customLoading}
                    sx={{
                      height: '56px',
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0'
                      }
                    }}
                  >
                    <MDTypography variant="button" fontWeight="medium" color="white">
                      {customLoading ? "Loading..." : "Check Fire Danger"}
                    </MDTypography>
                  </Button>
                </Grid>
              </Grid>
              
              {customError && (
                <MDBox mt={2}>
                  <MDTypography variant="body2" color="error">
                    {customError}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </MDBox>
        
        {/* Custom Location Results */}
        {customLocationResult && !customError && (
          <>
            <MDBox mt={4} mb={2}>
              <MDTypography variant="h5" fontWeight="medium" color="dark" textAlign="center">
                Wildfire Risk for {customLocationResult.location}
              </MDTypography>
              <MDTypography variant="body2" color="text" textAlign="center" mt={1}>
                Date: {customLocationResult.date}
              </MDTypography>
            </MDBox>
            
            {/* Custom Location Weather Data */}
            <MDBox mb={3}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={3}>
                    Current Weather
                  </MDTypography>
                  
                  <Grid container spacing={3}>
                    {/* Temperature */}
                    <Grid item xs={12} sm={6} md={2}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Temperature
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {customLocationResult.weather.temperature}°F
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Humidity */}
                    <Grid item xs={12} sm={6} md={2}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Humidity
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {customLocationResult.weather.humidity}%
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Wind Speed */}
                    <Grid item xs={12} sm={6} md={2}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Wind Speed
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {customLocationResult.weather.windspeed} mph
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Precipitation */}
                    <Grid item xs={12} sm={6} md={2}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Precipitation
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {customLocationResult.weather.precip} in
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Solar Radiation */}
                    {customLocationResult.weather.solarradiation !== undefined && customLocationResult.weather.solarradiation !== null && (
                      <Grid item xs={12} sm={6} md={2}>
                        <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                          <MDTypography variant="button" color="text" fontWeight="medium">
                            Solar Radiation
                          </MDTypography>
                          <MDTypography variant="h5" fontWeight="medium" mt={1}>
                            {customLocationResult.weather.solarradiation} W/m²
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    )}
                    
                    {/* Day Length */}
                    {customLocationResult.weather.dayLength !== undefined && customLocationResult.weather.dayLength !== null && (
                      <Grid item xs={12} sm={6} md={2}>
                        <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                          <MDTypography variant="button" color="text" fontWeight="medium">
                            Day Length
                          </MDTypography>
                          <MDTypography variant="h5" fontWeight="medium" mt={1}>
                            {customLocationResult.weather.dayLength.toFixed(2)} hours
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    )}
                  </Grid>
                </MDBox>
              </Card>
            </MDBox>
            
            {/* Custom Location Wildfire Prediction Cards */}
            <Grid container spacing={3} marginTop={2}>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color="warning"
                    icon="local_fire_department"
                    title="Fire Weather Index (FWI)"
                    count={customLocationResult.wildfire ? formatNumber(customLocationResult.wildfire.FWI.toFixed(2)) : "--"}
                    percentage={{
                      color: "success",
                      amount: "",
                      label: "",
                    }}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color="error"
                    icon="trending_up"
                    title="Standardized FWI (FWI_z)"
                    count={customLocationResult.wildfire ? formatNumber(customLocationResult.wildfire.FWI_z.toFixed(2)) : "--"}
                    percentage={{
                      color: "success",
                      amount: "",
                      label: "",
                    }}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color={
                      customLocationResult.wildfire?.risk_category === "Extreme" ? "error" :
                      customLocationResult.wildfire?.risk_category === "High" ? "warning" :
                      customLocationResult.wildfire?.risk_category === "Moderate" ? "info" : "success"
                    }
                    icon="warning"
                    title="Risk Category"
                    count={customLocationResult.wildfire ? customLocationResult.wildfire.risk_category : "--"}
                    percentage={{
                      color: "success",
                      amount: "",
                      label: "",
                    }}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color="dark"
                    icon="whatshot"
                    title="Ignition Probability"
                    count={customLocationResult.wildfire ? `${(customLocationResult.wildfire.ignition_probability * 100).toFixed(2)}%` : "--"}
                    percentage={{
                      color: "success",
                      amount: "",
                      label: "",
                    }}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </>
        )}
      </MDBox>

      {/* Adaptive Detection Threshold Section */}
      {wildfirePrediction && (
        <AdaptiveThresholdSection 
          riskCategory={wildfirePrediction.risk_category}
          fwiZ={wildfirePrediction.FWI_z}
        />
      )}

      {/* Satellite Context Section */}
      <SatelliteContextSection />

      <Footer />
    </DashboardLayout>
  );
}

export default Wildfire;
